/* eslint-env commonjs */

const STRUCTURAL_ROLE_PATTERN =
  /\b(?:doc-pagebreak|pagebreak|page-break|page-list|doc-endnote|doc-footnote|doc-noteref|doc-biblioref|toc|landmark)\b/i;
const STRUCTURAL_META_PATTERN =
  /\b(?:pagebreak|page-break|pagenum|page-num|page_num|page-list|noteref|note-ref|footnote|endnote|annotation|landmark|toc|table-of-contents|contents-nav)\b/i;

function normalizeNarrationText(value) {
  return String(value || "")
    .split("\u0000")
    .join("")
    .replace(/\f/g, "\n\n")
    .split("\u000b")
    .join("\n")
    .replace(/[\u00ad\u200b-\u200f\u2060\ufeff]/g, "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function isSkippableStructuralText(value) {
  const normalized = normalizeNarrationText(value);

  if (!normalized) {
    return true;
  }

  const compact = normalized.replace(/\s+/g, "");

  if (!/[A-Za-z0-9\u00C0-\u024F\u3400-\u9fff]/.test(normalized)) {
    return true;
  }

  if (/^(?:page|pg\.?)\s*[-–—]?\s*\d{1,4}\s*[-–—]?$/i.test(normalized)) {
    return true;
  }

  if (
    /^(?:page|pg\.?|p\.)\s*[-–—]?\s*\d{1,4}(?:\s*(?:\/|of)\s*\d{1,4})?\s*[-–—]?$/i.test(
      normalized
    )
  ) {
    return true;
  }

  if (/^第\s*\d{1,4}\s*[页頁]$/i.test(normalized)) {
    return true;
  }

  if (/^[-–—]?\s*\d{1,4}\s*[-–—]?$/.test(normalized)) {
    return true;
  }

  if (/^\d{1,4}\s*\/\s*\d{1,4}$/.test(normalized)) {
    return true;
  }

  if (/^[-–—_*~·•.=]{2,}$/.test(compact)) {
    return true;
  }

  return false;
}

function sanitizeNarrationSourceText(value) {
  const normalized = normalizeNarrationText(value);

  if (!normalized) {
    return "";
  }

  const cleanedLines = [];
  let previousWasBlank = true;

  normalized.split("\n").forEach((line) => {
    const cleanedLine = normalizeNarrationText(line);

    if (!cleanedLine) {
      if (cleanedLines.length && !previousWasBlank) {
        cleanedLines.push("");
        previousWasBlank = true;
      }

      return;
    }

    if (isSkippableStructuralText(cleanedLine)) {
      return;
    }

    cleanedLines.push(cleanedLine);
    previousWasBlank = false;
  });

  return cleanedLines.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

function getNodeMeta(node) {
  const attributes = [
    node?.getAttribute?.("epub:type"),
    node?.getAttribute?.("type"),
    node?.getAttribute?.("role"),
    node?.getAttribute?.("class"),
    node?.getAttribute?.("id")
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return {
    tagName: String(node?.localName || node?.nodeName || "").toLowerCase(),
    attributes
  };
}

function shouldSkipMarkupNode(node) {
  if (!node) {
    return true;
  }

  if (node.hidden || node.getAttribute?.("hidden") !== null) {
    return true;
  }

  if (String(node.getAttribute?.("aria-hidden") || "").toLowerCase() === "true") {
    return true;
  }

  const meta = getNodeMeta(node);

  if (["script", "style", "svg", "nav", "audio", "video", "noscript"].includes(meta.tagName)) {
    return true;
  }

  if (STRUCTURAL_ROLE_PATTERN.test(meta.attributes) || STRUCTURAL_META_PATTERN.test(meta.attributes)) {
    return true;
  }

  if (typeof node.closest === "function" && node.closest("nav")) {
    return true;
  }

  return false;
}

module.exports = {
  normalizeNarrationText,
  isSkippableStructuralText,
  sanitizeNarrationSourceText,
  shouldSkipMarkupNode
};
